/**
 * Édition d'une routine (`ARCHITECTURE_V2.md` §4.1 et §4.3, specs §12.2).
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 🔴 UNE ROUTINE EST UNE LISTE D'ITEMS — UN EXERCICE, OU UN GROUPE
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Deux boutons côte à côte, et c'est tout le modèle mental (retours du
 * 28/08/2026) :
 *
 *   « Ajouter un exercice » → l'exercice entre **directement** dans la routine ;
 *   « Ajouter un bloc »     → un groupe, superset ou circuit, qu'on remplit.
 *
 * Auparavant il fallait créer un bloc, choisir « Exercice seul », **puis**
 * ajouter l'exercice : trois gestes et une notion à comprendre pour le cas le
 * plus courant de tous. La nature `single` existe toujours en base — une
 * routine reste une liste ordonnée de blocs, un exercice isolé est un bloc à
 * une ligne — mais elle a cessé d'être un choix offert. **C'est l'emballage qui
 * devient invisible, pas le modèle qui change** : des lignes rattachées
 * directement à la routine auraient demandé d'ordonner deux tables dans un même
 * espace de positions, pour un gain nul à l'écran.
 *
 * ⚠️ Conséquence à l'affichage : un bloc `single` ne montre ni son type ni le
 * mot « bloc ». Il se lit comme une carte d'exercice, avec son repos.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 🔴 RÉPÉTITIONS **OU** DURÉE — jamais les deux
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Un gainage ne se compte pas en répétitions. Chaque ligne porte donc une
 * bascule, et l'éditeur n'affiche que les champs de la mesure retenue. En base,
 * c'est `duration_seconds IS NOT NULL` qui décide (pas de drapeau), et le
 * serveur refuse une ligne qui porterait les deux ou aucune
 * (`measure_required`).
 *
 * ⚠️ **Aucune détection automatique d'après le nom.** Deviner « en durée » sur
 * « plank » serait juste une fois sur deux, faux en silence l'autre fois, et
 * impossible à comprendre depuis l'écran. Une bascule visible coûte un tap.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 🔴 RÉORDONNER PAR BOUTONS ↑ ↓, PAS PAR GLISSER-DÉPOSER
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Trois raisons, dont chacune suffirait :
 *
 *   - **l'écran porte déjà des gestes.** Le pager entre journées et le
 *     « glisser pour supprimer » d'une entrée se disputent déjà le doigt sur
 *     l'axe horizontal (`useSwipeToDelete`, `stopPropagation` au `pointerdown`) ;
 *     un troisième geste, vertical cette fois, dans une feuille qui **défile**
 *     verticalement, serait une quatrième partie à cette dispute ;
 *   - **un glisser-déposer se déclenche par appui long, et personne ne le
 *     devine.** C'est le raisonnement déjà tenu pour la croix de suppression au
 *     pointeur fin (`useFinePointer`) : une affordance invisible n'en est pas
 *     une ;
 *   - **deux boutons sont atteignables au clavier**, et composer une routine
 *     est typiquement ce qu'on fait sur l'ordinateur.
 *
 * ⚠️ Le premier item n'a pas de ↑, le dernier pas de ↓ — **désactivés, pas
 * masqués** : une rangée de boutons dont la composition change d'une ligne à
 * l'autre se relit à chaque fois. Un contrôle désactivé est d'ailleurs le seul
 * cas où WCAG 2.1 exempte le contraste (§1.4.3).
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 🔴 UN FORMULAIRE, UN ENREGISTREMENT
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Toute la routine part en **un seul `PUT`**. Ce n'est pas une économie
 * d'appels : c'est ce qui rend l'écriture atomique. Et c'est possible **sans un
 * seul place-titre** parce que chaque bloc et chaque ligne reçoit son
 * `clientId` au moment où on l'ajoute ici (§2.3-b, `routineDraft.ts`).
 */

import { useState } from 'react';

import type { ProgressionKind, RoutineBlockKind, SetKind } from '../../../../shared/types';
import { Button, GlyphClose, GlyphPlus } from '../../components/nourri';
import { ExerciseThumb } from './ExerciseThumb';
import { Field, Hint, NumberField, Sheet } from '../../components/ui/primitives';
import { ExercisePickerSheet } from './ExercisePickerSheet';
import {
  BLOCK_KIND_LABEL,
  GROUP_KINDS,
  groupBlock,
  lineFor,
  PROGRESSION_LABEL,
  routineDraftIssue,
  SET_KIND_LABEL,
  singleBlock,
  type BlockDraft,
  type LineDraft,
  type RoutineDraft,
} from './routineDraft';

/**
 * ⚠️ **Quatrième copie de ce petit `<select>` étiqueté** (après
 * `ExerciseEditor`, `PlanningSection` et `NotificationsSection`). Elle est
 * recopiée plutôt qu'extraite, pour ne pas réécrire trois écrans en service
 * dans une étape qui en ajoute déjà un — mais c'est le moment de le noter :
 * **la cinquième doit déménager dans `components/ui/primitives.tsx`**, à côté
 * de `Field` et `NumberField`, qui existent exactement pour ça.
 */
function Select<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
      <span className="n-field__label">{label}</span>
      <select
        value={value}
        onChange={(event) => {
          onChange(event.target.value as T);
        }}
        className="ui-select"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

const SET_KINDS = (Object.keys(SET_KIND_LABEL) as SetKind[]).map((value) => ({
  value,
  label: SET_KIND_LABEL[value],
}));

const PROGRESSIONS = (Object.keys(PROGRESSION_LABEL) as ProgressionKind[]).map((value) => ({
  value,
  label: PROGRESSION_LABEL[value],
}));

/** Déplace un élément d'un cran, sans muter le tableau reçu. */
function moved<T>(list: readonly T[], index: number, delta: number): T[] {
  const cible = index + delta;
  if (cible < 0 || cible >= list.length) return [...list];
  const copie = [...list];
  const [element] = copie.splice(index, 1);
  if (element !== undefined) copie.splice(cible, 0, element);
  return copie;
}

/** Rangée ↑ ↓ ✕ — la même sur un item de routine et sur une ligne de groupe. */
function OrderControls({
  quoi,
  index,
  total,
  onMove,
  onRemove,
}: {
  quoi: string;
  index: number;
  total: number;
  onMove: (delta: number) => void;
  onRemove: () => void;
}) {
  return (
    <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
      <Button
        variant="ghost"
        shape="circle"
        aria-label={`Monter ${quoi}`}
        disabled={index === 0}
        onClick={() => {
          onMove(-1);
        }}
      >
        ↑
      </Button>
      <Button
        variant="ghost"
        shape="circle"
        aria-label={`Descendre ${quoi}`}
        disabled={index === total - 1}
        onClick={() => {
          onMove(1);
        }}
      >
        ↓
      </Button>
      <Button variant="danger" shape="circle" aria-label={`Retirer ${quoi}`} onClick={onRemove}>
        <GlyphClose />
      </Button>
    </div>
  );
}

/* ==========================================================================
 * Une ligne
 * ======================================================================= */

function LineEditor({
  line,
  index,
  total,
  showOrder,
  onPatch,
  onMove,
  onRemove,
}: {
  line: LineDraft;
  index: number;
  total: number;
  /**
   * ⚠️ **Faux pour un exercice isolé** : ses ↑ ↓ ✕ sont ceux de l'item, portés
   * par la carte. Deux rangées de contrôles pour un seul objet feraient croire
   * qu'il y a deux choses à déplacer.
   */
  showOrder: boolean;
  onPatch: (patch: Partial<LineDraft>) => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
}) {
  return (
    /*
     * ⚠️ **Pas de cadre dans un cadre.** Un exercice isolé est déjà porté par sa
     * carte d'item ; lui redonner la bordure d'une ligne de groupe dessinerait
     * deux boîtes pour un seul objet. La variante `--plain` ne retire que la
     * décoration, jamais l'espacement entre les champs.
     */
    <div className={showOrder ? 'n-routine-line' : 'n-routine-line n-routine-line--plain'}>
      {showOrder && (
        <div className="n-row n-row--between">
          {/*
            🔴 **La vignette dans l'éditeur aussi** *(31/08/2026, demandé après
            usage)*. Elle était déjà dans la liste des exercices, dans le
            sélecteur et dans la lecture d'une routine ; il ne manquait que
            l'écran où l'on **compose**, c'est-à-dire celui où l'on hésite entre
            deux variantes du même mouvement. Le même composant qu'aux trois
            autres endroits — `ExerciseThumb` prend `mediaKey` et `imageCount`,
            pas un DTO, précisément pour ça.

            ⚠️ Un exercice sans image ne réserve **aucun** espace (§1.1) : le
            composant ne rend rien, et le `gap` de la rangée referme la place.
          */}
          <span className="n-routine-line__id">
            <ExerciseThumb
              mediaKey={line.exerciseMediaKey}
              imageCount={line.exerciseImageCount}
            />
            <span className="n-list__name">{line.exerciseName}</span>
          </span>
          <OrderControls
            quoi={line.exerciseName}
            index={index}
            total={total}
            onMove={onMove}
            onRemove={onRemove}
          />
        </div>
      )}

      <div className="n-routine-grid n-routine-grid--2">
        <NumberField
          label="Séries"
          value={line.sets}
          onChange={(sets) => {
            onPatch({ sets });
          }}
        />
        {/*
          🔴 **La bascule de mesure.** Deux puces plutôt qu'une liste
          déroulante : elles changent les champs juste en dessous, donc les deux
          possibilités doivent être visibles ensemble — même raisonnement que le
          type d'un groupe.
        */}
        <div
          className="n-chip-row"
          role="group"
          aria-label={`Mesure de ${line.exerciseName}`}
          style={{ alignSelf: 'end' }}
        >
          <Button
            variant={line.measure === 'reps' ? 'selected' : 'secondary'}
            aria-pressed={line.measure === 'reps'}
            onClick={() => {
              onPatch({ measure: 'reps' });
            }}
          >
            Répétitions
          </Button>
          <Button
            variant={line.measure === 'time' ? 'selected' : 'secondary'}
            aria-pressed={line.measure === 'time'}
            onClick={() => {
              onPatch({ measure: 'time' });
            }}
          >
            Durée
          </Button>
        </div>
      </div>

      {line.measure === 'reps' ? (
        <div className="n-routine-grid n-routine-grid--2">
          {/*
            ⚠️ **Deux champs, jamais un champ et une case « reps fixes »** : le
            §4.1 range les reps fixes comme le cas particulier de la plage
            (`repMin === repMax`), et l'inverse aurait imposé un `if` à chaque
            lecture. Ici l'utilisateur tape simplement deux fois le même nombre.
          */}
          <NumberField
            label="Reps min"
            value={line.repMin}
            onChange={(repMin) => {
              onPatch({ repMin });
            }}
          />
          <NumberField
            label="Reps max"
            value={line.repMax}
            onChange={(repMax) => {
              onPatch({ repMax });
            }}
          />
        </div>
      ) : (
        <NumberField
          label="Durée d’une série"
          suffix="s"
          value={line.durationSeconds}
          onChange={(durationSeconds) => {
            onPatch({ durationSeconds });
          }}
        />
      )}

      <div className="n-routine-grid n-routine-grid--2">
        {/*
          ⚠️ Champ **vide** ≠ « 0 kg » (`toOptional` dans `routineDraft.ts`) :
          0 kg est une valeur — l'exercice au poids du corps —, tandis qu'un
          champ vide veut dire « je ne me fixe pas de charge ».
        */}
        <NumberField
          label="Charge visée"
          suffix="kg"
          value={line.targetWeightKg}
          onChange={(targetWeightKg) => {
            onPatch({ targetWeightKg });
          }}
          placeholder="—"
        />
        <NumberField
          label="RPE visé"
          value={line.targetRpe}
          onChange={(targetRpe) => {
            onPatch({ targetRpe });
          }}
          placeholder="—"
        />
      </div>

      <div className="n-routine-grid n-routine-grid--2">
        <Select
          label="Type de série"
          value={line.setKind}
          options={SET_KINDS}
          onChange={(setKind) => {
            onPatch({ setKind });
          }}
        />
        <Select
          label="Progression"
          value={line.progressionKind}
          options={PROGRESSIONS}
          onChange={(progressionKind) => {
            onPatch({ progressionKind });
          }}
        />
      </div>

      {/*
        ⚠️ L'incrément n'apparaît **que** si une progression est demandée. Le
        serveur le met à `null` dans l'autre cas (§4.3) ; afficher un champ dont
        la valeur ne sera pas conservée serait un mensonge de formulaire.
      */}
      {line.progressionKind === 'double' && (
        <NumberField
          label="Incrément par progression"
          suffix="kg"
          value={line.progressionIncrementKg}
          onChange={(progressionIncrementKg) => {
            onPatch({ progressionIncrementKg });
          }}
        />
      )}

      <Field
        label="Note"
        value={line.note}
        onChange={(note) => {
          onPatch({ note });
        }}
        placeholder="Tempo lent, prise serrée…"
      />
    </div>
  );
}

/* ==========================================================================
 * Un item de routine — un exercice isolé, ou un groupe
 * ======================================================================= */

function BlockEditor({
  block,
  index,
  total,
  onPatch,
  onMove,
  onRemove,
  onAddExercise,
}: {
  block: BlockDraft;
  index: number;
  total: number;
  onPatch: (patch: Partial<BlockDraft>) => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
  onAddExercise: () => void;
}) {
  const isole = block.kind === 'single';
  const premiere = block.lines[0];
  const titre = isole ? (premiere?.exerciseName ?? 'Exercice') : `Bloc ${String(index + 1)}`;

  const patchLine = (clientId: string, patch: Partial<LineDraft>) => {
    onPatch({
      lines: block.lines.map((candidate) =>
        candidate.clientId === clientId ? { ...candidate, ...patch } : candidate,
      ),
    });
  };

  return (
    <div className="n-routine-block">
      <div className="n-row n-row--between">
        {/*
          ⚠️ Un exercice isolé se nomme par son exercice, pas par « Bloc 3 » :
          l'utilisateur n'a jamais choisi de créer un bloc, il a ajouté un
          exercice.
        */}
        {/*
          ⚠️ **La vignette seulement sur un exercice ISOLÉ.** Un bloc s'intitule
          « Bloc 2 » et groupe plusieurs mouvements : lui coller l'image du
          premier laisserait croire qu'elle vaut pour le bloc entier. Les lignes
          du groupe portent chacune la leur, juste en dessous.
        */}
        {isole ? (
          <span className="n-routine-line__id">
            <ExerciseThumb
              mediaKey={premiere?.exerciseMediaKey ?? null}
              imageCount={premiere?.exerciseImageCount ?? 0}
            />
            <span className="n-list__name">{titre}</span>
          </span>
        ) : (
          <span className="n-eyebrow">{titre}</span>
        )}
        <OrderControls
          quoi={isole ? titre : `le bloc ${String(index + 1)}`}
          index={index}
          total={total}
          onMove={onMove}
          onRemove={onRemove}
        />
      </div>

      {/*
        Le type de groupe en puces plutôt qu'en liste déroulante : deux valeurs
        qui changent la forme du bloc au-dessous, donc elles doivent être
        visibles ensemble. ⚠️ **« Exercice seul » n'y figure plus** — il n'est
        plus un choix, il est ce qu'on obtient en ajoutant un exercice.
      */}
      {!isole && (
        <div className="n-chip-row" role="group" aria-label={`Type du bloc ${String(index + 1)}`}>
          {GROUP_KINDS.map((kind: RoutineBlockKind) => (
            <Button
              key={kind}
              variant={block.kind === kind ? 'selected' : 'secondary'}
              aria-pressed={block.kind === kind}
              onClick={() => {
                onPatch({ kind });
              }}
            >
              {BLOCK_KIND_LABEL[kind]}
            </Button>
          ))}
        </div>
      )}

      <div className="n-routine-grid n-routine-grid--2">
        {/*
          ⚠️ **Les tours n'existent que pour un circuit** (§4.3) : un superset
          les *ignore* — son nombre de tours est celui des séries. Afficher le
          champ quand même laisserait croire qu'il compte.
        */}
        {block.kind === 'circuit' && (
          <NumberField
            label="Tours"
            value={block.rounds}
            onChange={(rounds) => {
              onPatch({ rounds });
            }}
          />
        )}
        {/*
          🔴 **Le repos est saisi sur le BLOC, jamais sur la ligne** (§4.3) :
          dans un superset, valider la première série ne déclenche aucun repos.
          C'est cette valeur que lira le chronomètre de l'étape 6 — y compris
          pour un exercice isolé, dont le bloc est invisible mais bien là.
        */}
        <NumberField
          label={isole ? 'Repos entre séries' : 'Repos entre tours'}
          suffix="s"
          value={block.restSeconds}
          onChange={(restSeconds) => {
            onPatch({ restSeconds });
          }}
          placeholder="—"
        />
      </div>

      {block.lines.map((line, i) => (
        <LineEditor
          key={line.clientId}
          line={line}
          index={i}
          total={block.lines.length}
          showOrder={!isole}
          onPatch={(patch) => {
            patchLine(line.clientId, patch);
          }}
          onMove={(delta) => {
            onPatch({ lines: moved(block.lines, i, delta) });
          }}
          onRemove={() => {
            onPatch({
              lines: block.lines.filter((candidate) => candidate.clientId !== line.clientId),
            });
          }}
        />
      ))}

      {/*
        ⚠️ Un exercice isolé n'a pas de bouton d'ajout : en ajouter un second
        ferait de lui un superset qui ne dit pas son nom, et le serveur le
        refuserait (`single_has_many`). Pour en enchaîner deux, on crée un bloc.
      */}
      {!isole && (
        <Button variant="ghost" shape="rounded" block onClick={onAddExercise}>
          <GlyphPlus /> Ajouter un exercice au bloc
        </Button>
      )}
    </div>
  );
}

/* ==========================================================================
 * La feuille
 * ======================================================================= */

/** Où ira le prochain exercice choisi : dans un bloc, ou seul dans la routine. */
type Picking = { cible: 'routine' } | { cible: 'bloc'; clientId: string };

export function RoutineEditorSheet({
  open,
  title,
  draft,
  onDraftChange,
  saving,
  onSubmit,
  onClose,
}: {
  open: boolean;
  title: string;
  draft: RoutineDraft;
  onDraftChange: (next: RoutineDraft) => void;
  saving: boolean;
  onSubmit: () => void;
  onClose: () => void;
}) {
  const [picking, setPicking] = useState<Picking | null>(null);

  const issue = routineDraftIssue(draft);

  const patchBlock = (clientId: string, patch: Partial<BlockDraft>) => {
    onDraftChange({
      ...draft,
      blocks: draft.blocks.map((candidate) =>
        candidate.clientId === clientId ? { ...candidate, ...patch } : candidate,
      ),
    });
  };

  return (
    <>
      <Sheet open={open} title={title} onClose={onClose}>
        <Field
          label="Nom de la routine"
          value={draft.name}
          onChange={(name) => {
            onDraftChange({ ...draft, name });
          }}
          placeholder="Haut du corps A"
        />

        <Field
          label="Note"
          value={draft.notes}
          onChange={(notes) => {
            onDraftChange({ ...draft, notes });
          }}
          /*
            ⚠️ **L'exemple ne parle plus d'échauffement** : il a son champ
            depuis le 29/08/2026, et laisser l'ancien exemple ici enverrait
            l'échauffement dans la note — c'est-à-dire à l'endroit exact d'où on
            vient de le sortir.
          */
          placeholder="Jour lourd — reposer 3 min entre les blocs"
        />

        {/*
          🔴 **L'échauffement a son propre champ** *(29/08/2026, demandé après
          usage)*, et pas un repli sur la note. Les deux sont du texte libre sur
          la routine, et c'est pourquoi il fallait trancher : un échauffement se
          lit **avant** de commencer et se suit dans l'ordre, une note se lit
          quand on en a besoin. Les fondre obligerait l'écran de séance à
          deviner laquelle des deux il montre au démarrage.

          ⚠️ **Une étape par ligne**, comme les étapes d'une recette (étape 7)
          et l'exécution d'un exercice. La même forme pour la même chose.
        */}
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          <span className="n-field__label">Échauffement — une étape par ligne</span>
          <textarea
            value={draft.warmup}
            onChange={(event) => {
              onDraftChange({ ...draft, warmup: event.target.value });
            }}
            rows={4}
            placeholder={'5 min de rameur\nRotations d’épaules\nBarre à vide, 2 × 10'}
            className="ui-textarea"
          />
        </label>

        {draft.blocks.map((block, index) => (
          <BlockEditor
            key={block.clientId}
            block={block}
            index={index}
            total={draft.blocks.length}
            onPatch={(patch) => {
              patchBlock(block.clientId, patch);
            }}
            onMove={(delta) => {
              onDraftChange({ ...draft, blocks: moved(draft.blocks, index, delta) });
            }}
            onRemove={() => {
              onDraftChange({
                ...draft,
                blocks: draft.blocks.filter((c) => c.clientId !== block.clientId),
              });
            }}
            onAddExercise={() => {
              setPicking({ cible: 'bloc', clientId: block.clientId });
            }}
          />
        ))}

        {/*
          🔴 **Les deux gestes de composition, côte à côte.** « Ajouter un
          exercice » est le cas courant et il est à gauche ; « Ajouter un bloc »
          ne sert qu'à enchaîner plusieurs exercices sans repos.
        */}
        <div className="n-routine-grid n-routine-grid--2">
          <Button
            variant="primary"
            shape="rounded"
            block
            onClick={() => {
              setPicking({ cible: 'routine' });
            }}
          >
            <GlyphPlus /> Ajouter un exercice
          </Button>
          <Button
            variant="secondary"
            shape="rounded"
            block
            onClick={() => {
              /*
               * 🔴 `clientId` tiré **ici**, à l'ajout — pas à l'enregistrement
               * (§2.3-b). C'est ce qui rend la composition possible hors ligne,
               * et ce qui fait qu'un double tap sur « Enregistrer » réécrit le
               * même bloc au lieu d'en créer un second.
               */
              onDraftChange({ ...draft, blocks: [...draft.blocks, groupBlock()] });
            }}
          >
            Ajouter un bloc
          </Button>
        </div>

        <Hint>
          Un bloc enchaîne plusieurs exercices sans repos — superset ou circuit. Pour un
          exercice à la suite des autres, « Ajouter un exercice » suffit.
        </Hint>

        {/*
          ⚠️ Le motif est affiché, pas seulement le bouton grisé. Avec jusqu'à
          trente lignes, un bouton muet obligerait à chercher soi-même laquelle
          cloche — et hors ligne, l'aller-retour qui l'aurait dit n'arriverait
          qu'au drainage.
        */}
        {issue !== null && <Hint>{issue}</Hint>}

        <Button
          variant="primary"
          shape="rounded"
          block
          disabled={issue !== null || saving}
          onClick={onSubmit}
        >
          {saving ? 'Enregistrement…' : 'Enregistrer la routine'}
        </Button>

        <Hint>
          Une routine peut rester vide : c’est une routine qu’on compose. Un bloc, lui,
          n’existe pas sans exercice.
        </Hint>
      </Sheet>

      {/*
        ⚠️ La feuille de choix est rendue **hors** de celle de l'éditeur : deux
        `Sheet` imbriquées produiraient deux portails l'un dans l'autre, et le
        compteur de `useHideChromeWhileOpen` s'en accommode déjà (« deux
        feuilles peuvent se chevaucher le temps d'une transition »).
      */}
      <ExercisePickerSheet
        open={picking !== null}
        onClose={() => {
          setPicking(null);
        }}
        onPick={(exercise) => {
          /*
            ⚠️ **La vignette est reprise telle que le sélecteur la rend.** Il la
            portait déjà — on la jetait. Rien de nouveau n'est demandé au
            réseau, et l'exercice s'illustre dès l'instant où on le choisit,
            avant même le premier enregistrement.
          */
          const ligne = lineFor(exercise.clientId, exercise.name, {
            mediaKey: exercise.mediaKey,
            imageCount: exercise.imageCount,
          });

          if (picking?.cible === 'routine') {
            // Un exercice isolé : son bloc naît avec lui, et ne se voit pas.
            onDraftChange({
              ...draft,
              blocks: [...draft.blocks, { ...singleBlock(), lines: [ligne] }],
            });
          } else if (picking?.cible === 'bloc') {
            const cible = draft.blocks.find((block) => block.clientId === picking.clientId);
            if (cible) patchBlock(cible.clientId, { lines: [...cible.lines, ligne] });
          }

          setPicking(null);
        }}
      />
    </>
  );
}
